/**
 * Digital receipt — `/receipt/:token`.
 *
 * Draws one composed print (a receipt, a guest check, a merchant copy, or a
 * kitchen chit for a station with no printer) as the
 * paper it prints as, from the document the server composed and kept on its
 * print record. Same renderer as the management Ticket Appearance preview
 * (`renderPaperGrid` + `PaperPreview`), which the preview-grid parity corpus
 * holds equal to the gateway's ESC/POS output — so what a guest sees here is
 * what the printer would have handed them.
 *
 * It works with no printer and no gateway: a till with nothing to print on
 * sends the receipt DIGITAL and shows this link (or its QR) instead.
 *
 * Public, like `CheckPayPage`: the signed token in the path is the whole
 * credential, so the page mounts OUTSIDE `<CustomerSession>` (App.tsx) and the
 * read goes through the Firebase-free public client. Read-only — nothing here
 * moves money or writes anything.
 */

import {
  Button,
  decodeGrayPngBase64,
  type GrayImage,
  PaperPreview,
  renderPaperGrid,
  Skeleton,
} from '@order-up/common';
import { isPrintViewMiss, usePrintView } from '@order-up/client/vite';
import type { PrintDocument } from '@order-up/models';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const TYPE_LABELS: Record<string, string> = {
  customer_receipt: 'Receipt',
  merchant_receipt: 'Merchant copy',
  guest_check: 'Guest check',
  void_receipt: 'Void receipt',
  refund_receipt: 'Refund receipt',
  tip_adjustment: 'Tip adjustment',
  kitchen_ticket: 'Kitchen ticket',
  bar_ticket: 'Bar ticket',
  expo_ticket: 'Expo ticket',
  fire_ticket: 'Fire ticket',
  void_kitchen_ticket: 'Void ticket',
};

/** Every logo image in the document, so each can be decoded before the paper is drawn. */
function logoImagesOf(document: PrintDocument | undefined): string[] {
  if (!document) return [];
  const out: string[] = [];
  for (const block of document.blocks) {
    if (block.kind === 'logo' && block.imageBase64) out.push(block.imageBase64);
  }
  return out;
}

export default function ReceiptViewPage() {
  const { token = '' } = useParams<{ token: string }>();
  const view = usePrintView(token);
  const data = view.data;

  // Logos decode asynchronously; the paper waits for them so it never redraws
  // from a placeholder to the artwork in front of the guest.
  const logos = useMemo(() => logoImagesOf(data?.document), [data?.document]);
  const [decoded, setDecoded] = useState<Map<string, GrayImage | null> | null>(null);
  useEffect(() => {
    let live = true;
    void Promise.all(logos.map(async (b64) => [b64, await decodeGrayPngBase64(b64)] as const)).then((pairs) => {
      if (live) setDecoded(new Map(pairs));
    });
    return () => {
      live = false;
    };
  }, [logos]);

  const grid = useMemo(() => {
    if (!data || !decoded) return null;
    try {
      return renderPaperGrid(
        data.document,
        { paperWidthMm: data.paperWidthMm },
        { decodeLogo: (b64) => decoded.get(b64) ?? undefined },
      );
    } catch {
      return null;
    }
  }, [data, decoded]);

  const station = (data?.document.meta as { stationName?: string } | undefined)?.stationName;
  const kind = data ? (TYPE_LABELS[data.type] ?? 'Receipt') : 'Receipt';
  const title = station ? `${kind} · ${station}` : kind;
  const when = data?.createdAtMs ? new Date(data.createdAtMs).toLocaleString() : null;
  const miss = view.isError && isPrintViewMiss(view.error);

  return (
    <main className="min-h-dvh bg-muted/40 px-4 py-6 print:bg-white print:p-0">
      <div className="mx-auto flex w-full max-w-md flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-2 print:hidden">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold">{title}</h1>
            <p className="text-sm text-muted-foreground" data-testid="receipt-subtitle">
              {data?.orderNumber !== undefined ? `Order #${data.orderNumber}` : null}
              {data?.orderNumber !== undefined && when ? ' · ' : null}
              {when}
            </p>
          </div>
          {grid ? (
            <Button type="button" variant="outline" size="sm" onClick={() => window.print()}>
              Print or save as PDF
            </Button>
          ) : null}
        </header>

        {view.isLoading || (data && !decoded) ? (
          <div role="status" aria-busy="true" aria-label="Loading the receipt">
            <Skeleton className="h-[32rem] w-full rounded-sm" />
          </div>
        ) : miss ? (
          <section className="rounded-lg border bg-background p-6 text-center" data-testid="receipt-miss">
            <h2 className="font-medium">This receipt link is not available</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              It may have expired. Ask the restaurant to send the receipt again.
            </p>
          </section>
        ) : grid && data ? (
          <PaperPreview grid={grid} label={`${kind} on ${data.paperWidthMm} mm paper`} />
        ) : (
          <section className="rounded-lg border bg-background p-6 text-center" data-testid="receipt-error">
            <h2 className="font-medium">The receipt could not be shown</h2>
            <p className="mt-1 text-sm text-muted-foreground">Check your connection and reload the page.</p>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => void view.refetch()}>
              Try again
            </Button>
          </section>
        )}

        {data?.digital ? (
          <p className="text-center text-xs text-muted-foreground print:hidden">
            {data.type.endsWith('_ticket') ? 'A digital kitchen ticket' : 'A digital receipt'} — exactly as it would print.
          </p>
        ) : null}
      </div>
    </main>
  );
}
